import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { PaymentOrder } from '../../../types';
import Input from '../Input';
import Select from '../Select';
import TextArea from '../TextArea';

interface PaymentOrderFooterSectionsProps {
  register: UseFormRegister<PaymentOrder>;
  errors: FieldErrors<PaymentOrder>;
  statusOptions: { value: string; label: string }[];
}

export const TotalsSection: React.FC<{ register: UseFormRegister<PaymentOrder>; errors: FieldErrors<PaymentOrder> }> = ({
  register,
  errors,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Totals</h2>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <Input
              label="Subtotal"
              type="number"
              step="0.01"
              {...register('subtotal')}
              disabled
            />
          </div>
          <div>
            <Input
              label="Taxes (14%)"
              type="number"
              step="0.01"
              {...register('taxes')}
              disabled
            />
          </div>
          <div>
            <Input
              label="Discount"
              type="number"
              step="0.01"
              min="0"
              {...register('discount', { 
                min: { value: 0, message: 'Discount must be 0 or greater' }
              })}
              error={errors.discount?.message}
            />
          </div>
          <div>
            <Input
              label="Total"
              type="number"
              step="0.01"
              {...register('total')}
              disabled
              className="font-bold"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdditionalInfoSection: React.FC<{ register: UseFormRegister<PaymentOrder> }> = ({ register }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Additional Information</h2>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 gap-6">
          <div>
            <TextArea
              label="Comments or Additional Instructions"
              {...register('comments')}
              rows={3}
              placeholder="Any additional comments or instructions..."
            />
          </div>
          <div>
            <TextArea
              label="Additional Instructions"
              {...register('additionalInstructions')}
              rows={2}
              placeholder="Special instructions for payment processing..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthorizationSection: React.FC<{ register: UseFormRegister<PaymentOrder>; errors: FieldErrors<PaymentOrder> }> = ({
  register,
  errors,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Authorization</h2>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Input
              label="Authorized By (Name)"
              {...register('authorizedBy.name', { required: 'Authorized by name is required' })}
              error={errors.authorizedBy?.name?.message}
            />
          </div>
          <div>
            <Input
              label="Job Title"
              {...register('authorizedBy.jobTitle', { required: 'Job title is required' })}
              error={errors.authorizedBy?.jobTitle?.message}
            />
          </div>
          <div>
            <Input
              label="Signature"
              {...register('authorizedBy.signature')}
              placeholder="Digital signature or initials"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const StatusPersonnelSection: React.FC<PaymentOrderFooterSectionsProps> = ({
  register,
  errors,
  statusOptions,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Status & Personnel</h2>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Select
              label="Status"
              {...register('status', { required: 'Status is required' })}
              options={statusOptions}
              error={errors.status?.message}
            />
          </div>
          <div>
            <Input
              label="Requested By"
              {...register('requestedBy', { required: 'Requested by is required' })}
              error={errors.requestedBy?.message}
            />
          </div>
          <div>
            <Input
              label="Approved By"
              {...register('approvedBy')}
              placeholder="Approver name (if approved)"
            />
          </div>
          <div>
            <Input
              label="Paid By"
              {...register('paidBy')}
              placeholder="Person who processed payment (if paid)"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
